/**
 * useApiKeys Hook
 * 
 * Manages API keys for an organization with real-time updates.
 * 
 * @module hooks/useApiKeys
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot,
  type QuerySnapshot,
  type DocumentData 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from '@/lib/firebase';
import { functions } from '@/lib/firebase';
import type { ApiKey } from '@/lib/firebase-types';

// =============================================================================
// Types
// =============================================================================

interface UseApiKeysResult {
  /** List of API keys */
  keys: ApiKey[];
  /** Loading state */
  loading: boolean;
  /** Error message if any */
  error: string | null;
  /** Number of active keys */
  keyCount: number;
  /** Create a new API key */
  createKey: (label: string) => Promise<string>;
  /** Revoke an API key */
  revokeKey: (keyId: string) => Promise<void>;
  /** Refresh the keys list */
  refresh: () => void;
}

interface CreateApiKeyResponse {
  success: boolean;
  key?: string;
  keyId?: string;
  error?: string;
}

interface RevokeApiKeyResponse {
  success: boolean;
  error?: string;
}

// =============================================================================
// Hook
// =============================================================================

export function useApiKeys(orgId: string | undefined): UseApiKeysResult {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ---------------------------------------------------------------------------
  // Real-time Listener
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!orgId) {
      setKeys([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const keysRef = collection(db, 'organizations', orgId, 'api_keys');
    const keysQuery = query(keysRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      keysQuery,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const keysData: ApiKey[] = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        })) as ApiKey[];

        setKeys(keysData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching API keys:', err);
        setError('Failed to load API keys.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [orgId, refreshTrigger]);

  // ---------------------------------------------------------------------------
  // Computed Values
  // ---------------------------------------------------------------------------

  const keyCount = keys.filter((k) => k.status === 'active').length;

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  const createKey = useCallback(
    async (label: string): Promise<string> => {
      if (!orgId) {
        throw new Error('Organization not found.');
      }

      const createApiKey = httpsCallable<
        { label: string; orgId: string },
        CreateApiKeyResponse
      >(functions, 'createApiKey');

      const result = await createApiKey({ label, orgId });

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to create API key.');
      }

      if (!result.data.key) {
        throw new Error('No key returned from server.');
      }

      return result.data.key;
    },
    [orgId]
  );

  const revokeKey = useCallback(
    async (keyId: string): Promise<void> => {
      if (!orgId) {
        throw new Error('Organization not found.');
      }

      const revokeApiKey = httpsCallable<
        { keyId: string; orgId: string },
        RevokeApiKeyResponse
      >(functions, 'revokeApiKey');

      const result = await revokeApiKey({ keyId, orgId });

      if (!result.data.success) {
        throw new Error(result.data.error || 'Failed to revoke API key.');
      }
    },
    [orgId]
  );

  const refresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return {
    keys,
    loading,
    error,
    keyCount,
    createKey,
    revokeKey,
    refresh,
  };
}
